import {
  buildCargoHeatingOnboardingPayloadFromDetail,
  normalizeCargoHeatingOnboardingApiRow,
} from 'src/app/shared/helpers/cargo-heating-onboarding-payload.util';
import { buildEmissionOnboardingPayloadFromDetail } from 'src/app/shared/helpers/emission-onboarding-payload.util';
import { formatCargoApiValue, extractUploadedFilesFromRow } from './cargo-heating-onboarding-view.util';
import { buildDynamicCargoHeatingDetailSections } from './cargo-heating-onboarding-dynamic.util';
import {
  buildLiquidCargoDynamicTable,
  LIQUID_CARGO_EMPTY_MESSAGE,
} from './cargo-heating-liquid-cargo.util';

export type OnboardingViewMode = 'list' | 'port' | 'cargo_heating' | 'emission';

export interface OnboardingDetailRow {
  label: string;
  value: string;
  keys?: string[];
  readOnly?: boolean;
  type?: 'text' | 'number';
}

export interface OnboardingDetailTable {
  headers: string[];
  rows: string[][];
  columns: { header: string; key: string }[];
  columnKeys?: string[];
  rawRows?: Record<string, unknown>[];
  dynamic?: boolean;
  dataKey?: string;
  editable?: boolean;
}

export interface OnboardingDetailSection {
  id: string;
  title: string;
  rows: OnboardingDetailRow[];
  table?: OnboardingDetailTable | null;
  editable?: boolean;
  readOnly?: boolean;
  /** Span full grid width — used for sections with data tables. */
  spanFull?: boolean;
  /** Uploaded document list (uploaded-docs section). */
  files?: OnboardingUploadedFile[];
  /** Shown when table has no rows but section is visible. */
  emptyMessage?: string;
}

/** Treat common empty / placeholder display strings as "no data". */
export function isEmptyOnboardingDisplayValue(value: unknown): boolean {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value !== 'string') {
    return false;
  }
  const normalized = value.trim().toLowerCase();
  return (
    !normalized ||
    normalized === '—' ||
    normalized === '-' ||
    normalized === 'n/a' ||
    normalized === 'na' ||
    normalized === 'not available' ||
    normalized === 'null' ||
    normalized === 'undefined'
  );
}

/**
 * True when a detail card has at least one real value, table rows, or files.
 * Used to hide entirely empty service sections (all "Not Available").
 */
export function sectionHasMeaningfulContent(section: OnboardingDetailSection): boolean {
  const hasMeaningfulRow = (section.rows ?? []).some(
    (row) => !isEmptyOnboardingDisplayValue(row?.value),
  );
  if (hasMeaningfulRow) {
    return true;
  }
  if ((section.table?.rows?.length ?? 0) > 0) {
    return true;
  }
  if ((section.files?.length ?? 0) > 0) {
    return true;
  }
  return false;
}


export interface OnboardingUploadedFile {
  field: string;
  documentType: string;
  fileName: string;
  s3Url: string;
  mimeType?: string;
  sizeBytes?: number;
}

export interface OnboardingSectionSavePayload {
  sectionId: string;
  rows: OnboardingDetailRow[];
  table: OnboardingDetailTable | null;
}

export function pickField(
  row: Record<string, unknown> | null | undefined,
  ...keys: string[]
): string {
  if (!row) {
    return '—';
  }
  for (const key of keys) {
    const value = row[key];
    if (value === null || value === undefined || value === '') {
      continue;
    }
    if (typeof value === 'object') {
      return formatTableCellValue(value);
    }
    return String(value);
  }
  return '—';
}

export function pickImo(row: Record<string, unknown> | null | undefined): string {
  return pickField(row, 'vessel_imo', 'imo_number', 'imo', 'IMO');
}

export function pickVesselName(row: Record<string, unknown> | null | undefined): string {
  return pickField(row, 'vessel_name', 'vesselName', 'ship_name', 'shipName');
}

export function normalizeVesselList(data: unknown): Record<string, unknown>[] {
  if (!data) {
    return [];
  }
  if (Array.isArray(data)) {
    return data as Record<string, unknown>[];
  }
  const record = data as Record<string, unknown>;
  const nested = record['data'] ?? record['vessels'] ?? record['results'];
  return Array.isArray(nested) ? (nested as Record<string, unknown>[]) : [];
}

const EMISSION_API_FIELD_MAP: Record<string, string> = {
  speed_vref: 'speedVref',
  mcrmr: 'mcrmr',
  mcrme_lim: 'mcrmeLim',
  pme: 'pme',
  pme_lim: 'pmeLim',
  ppto: 'ppto',
  cfme: 'cfme',
  sfcme: 'sfcme',
  sfcme_lim: 'sfcmeLim',
  pae: 'pae',
  cfae: 'cfae',
  sfeae: 'sfeae',
  fuel_type_me: 'fuelTypeMe',
  fuel_type_ae: 'fuelTypeAe',
  ice_class: 'iceClass',
  fi: 'fi',
  fc: 'fc',
  fj: 'fj',
  fw: 'fw',
  fm: 'fm',
  ficsr: 'ficsr',
};

function emissionTimestamp(row: Record<string, unknown>): number {
  const raw = pickField(row, 'updated_at', 'submitted_at', 'created_at');
  if (raw === '—') {
    return 0;
  }
  const parsed = Date.parse(raw);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function buildEmissionParameters(
  row: Record<string, unknown>,
): Record<string, unknown> {
  const nested =
    (row['emission'] as Record<string, unknown> | undefined) ??
    (row['emission_parameters'] as Record<string, unknown> | undefined);
  const params: Record<string, unknown> = nested ? { ...nested } : {};

  Object.entries(EMISSION_API_FIELD_MAP).forEach(([snake, camel]) => {
    const snakeValue = row[snake];
    const camelValue = row[camel];
    const value =
      snakeValue !== undefined && snakeValue !== null
        ? snakeValue
        : camelValue !== undefined && camelValue !== null
          ? camelValue
          : params[snake] ?? params[camel];

    if (value === undefined || value === null) {
      return;
    }

    params[snake] = value;
    params[camel] = value;
  });

  return params;
}

/** Map flat emission API row (snake_case root fields) to UI model. */
export function mapEmissionApiRow(
  row: Record<string, unknown>,
): Record<string, unknown> {
  if (!row || typeof row !== 'object') {
    return {};
  }

  const imo = pickImo(row);
  const mapped: Record<string, unknown> = {
    ...row,
    imo: imo !== '—' ? imo : row['imo'],
    vessel_imo: imo !== '—' ? imo : row['vessel_imo'],
    vessel_name: pickVesselName(row) !== '—' ? pickVesselName(row) : row['vessel_name'],
    emission: buildEmissionParameters(row),
  };

  if (
    mapped['client_id'] != null &&
    mapped['client_id'] !== '' &&
    !mapped['client'] &&
    !mapped['client_name']
  ) {
    mapped['client_name'] = `Client #${mapped['client_id']}`;
  }

  return mapped;
}

/** Deduplicate emission vessels by IMO — keep the most recently updated record. */
export function dedupeEmissionVesselsByImo(
  vessels: Record<string, unknown>[],
): Record<string, unknown>[] {
  const byImo = new Map<string, Record<string, unknown>>();

  vessels.forEach((vessel) => {
    const imo = pickImo(vessel).replace(/\D/g, '');
    if (!imo) {
      return;
    }

    const existing = byImo.get(imo);
    if (!existing || emissionTimestamp(vessel) >= emissionTimestamp(existing)) {
      byImo.set(imo, vessel);
    }
  });

  return Array.from(byImo.values()).sort(
    (a, b) => emissionTimestamp(b) - emissionTimestamp(a),
  );
}

/** Normalize list response from GET /api/emission/vessel-onboarding */
export function normalizeEmissionVesselList(
  data: unknown,
): Record<string, unknown>[] {
  return dedupeEmissionVesselsByImo(
    normalizeVesselList(data).map((row) => mapEmissionApiRow(row)),
  );
}

/** Normalize detail response from GET /api/emission/vessel-onboarding?vessel_imo=… */
export function normalizeEmissionDetailRow(
  data: unknown,
): Record<string, unknown> {
  if (!data) {
    return {};
  }

  if (Array.isArray(data)) {
    const mapped = data.map((row) =>
      mapEmissionApiRow(row as Record<string, unknown>),
    );
    return dedupeEmissionVesselsByImo(mapped)[0] ?? {};
  }

  const record = normalizeDetailRow(data);
  return mapEmissionApiRow(record);
}

/** Resolve emission detail for a specific IMO — never returns another vessel's row. */
export function normalizeEmissionDetailRowForImo(
  expectedImo: string,
  data: unknown,
  fallback?: Record<string, unknown>,
): Record<string, unknown> {
  const expected = normalizeImoDigits(expectedImo);
  const candidates: Record<string, unknown>[] = [];

  const collect = (input: unknown): void => {
    if (!input) {
      return;
    }
    if (Array.isArray(input)) {
      input.forEach((item) => collect(item));
      return;
    }
    if (typeof input !== 'object') {
      return;
    }
    const record = input as Record<string, unknown>;
    candidates.push(mapEmissionApiRow(record));
    ['data', 'emission', 'emission_parameters', 'json', 'config', 'result'].forEach(
      (key) => collect(record[key]),
    );
  };

  collect(data);

  let matched =
    expected &&
    candidates.find(
      (row) => normalizeImoDigits(pickImo(row)) === expected,
    );

  if (!matched) {
    const detail = normalizeEmissionDetailRow(data);
    const detailImo = normalizeImoDigits(pickImo(detail));
    if (detailImo === expected) {
      matched = detail;
    } else if (!detailImo && Object.keys(detail).length && expected) {
      matched = detail;
    } else {
      matched = fallback ?? {};
    }
  }

  if (matched && expected) {
    const matchedImo = normalizeImoDigits(pickImo(matched));
    if (matchedImo && matchedImo !== expected) {
      matched = fallback ?? {};
    }
  }

  const merged = {
    ...(fallback ?? {}),
    ...matched,
  };

  if (expected) {
    merged['imo'] = expected;
    merged['vessel_imo'] = expected;
  }
  if (fallback?.['vessel_name'] && !merged['vessel_name']) {
    merged['vessel_name'] = fallback['vessel_name'];
  }

  return merged;
}

export function normalizeDetailRow(data: unknown): Record<string, unknown> {
  if (!data) {
    return {};
  }
  if (Array.isArray(data)) {
    return (data[0] as Record<string, unknown>) ?? {};
  }
  const record = data as Record<string, unknown>;
  if (record['data'] && typeof record['data'] === 'object') {
    const nested = record['data'];
    return Array.isArray(nested)
      ? ((nested[0] as Record<string, unknown>) ?? {})
      : (nested as Record<string, unknown>);
  }
  return record;
}

export function normalizeImoDigits(imo: unknown): string {
  return String(imo ?? '').replace(/\D/g, '');
}

function parseNestedJsonObject(value: unknown): Record<string, unknown> | null {
  if (!value) {
    return null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
      return null;
    }
    try {
      const parsed = JSON.parse(trimmed) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return null;
    }
    return null;
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function isMeaningfulCargoValue(value: unknown): boolean {
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === 'string' && value.trim() === '') {
    return false;
  }
  if (Array.isArray(value) && !value.length) {
    return false;
  }
  return true;
}

function mergePreferNonEmpty(
  base: Record<string, unknown>,
  overlay: Record<string, unknown>,
): Record<string, unknown> {
  const result = { ...base };
  Object.entries(overlay).forEach(([key, value]) => {
    if (isMeaningfulCargoValue(value)) {
      result[key] = value;
    } else if (!(key in result)) {
      result[key] = value;
    }
  });
  return result;
}

function extractCargoHeatingConfigFromSource(
  input: unknown,
): Record<string, unknown> {
  if (!input) {
    return {};
  }

  const layers: Record<string, unknown>[] = [];
  const collectLayer = (source: unknown): void => {
    const record = parseNestedJsonObject(source);
    if (!record || !Object.keys(record).length) {
      return;
    }
    layers.push(record);
    [
      'onboarding_data',
      'onboardingData',
      'data',
      'json',
      'config',
      'cargo_heating',
      'cargo_heating_config',
      'onboarding_config',
      'onboarding_form',
      'result',
      'output',
    ].forEach((key) => collectLayer(record[key]));
  };

  collectLayer(input);

  let merged: Record<string, unknown> = {};
  layers.forEach((layer) => {
    merged = mergePreferNonEmpty(merged, liftCargoHeatingNestedFields(layer));
  });

  return normalizeCargoHeatingOnboardingApiRow(merged);
}

/**
 * Merge cargo heating configuration from cargo-heating API, SC onboarding details,
 * and list fallbacks so the dashboard shows saved boiler/heating/tank data.
 */
export function mergeCargoHeatingDetailSources(options: {
  expectedImo: string;
  fallback?: Record<string, unknown>;
  cargoApi?: unknown;
  mainVesselDetails?: unknown;
  listVessel?: Record<string, unknown> | null;
}): Record<string, unknown> {
  const expected = normalizeImoDigits(options.expectedImo);
  let merged: Record<string, unknown> = { ...(options.fallback ?? {}) };

  if (options.listVessel && Object.keys(options.listVessel).length) {
    merged = mergePreferNonEmpty(
      merged,
      extractCargoHeatingConfigFromSource(options.listVessel),
    );
  }

  if (options.mainVesselDetails) {
    merged = mergePreferNonEmpty(
      merged,
      extractCargoHeatingConfigFromSource(options.mainVesselDetails),
    );
  }

  if (options.cargoApi) {
    merged = mergePreferNonEmpty(
      merged,
      extractCargoHeatingConfigFromSource(options.cargoApi),
    );
  }

  const normalized = normalizeCargoHeatingDetailRow(
    expected,
    merged,
    options.fallback,
  );

  if (expected) {
    normalized['imo'] = expected;
    normalized['vessel_imo'] = expected;
  }
  if (options.fallback?.['vessel_name'] && !normalized['vessel_name']) {
    normalized['vessel_name'] = options.fallback['vessel_name'];
  }

  return normalizeCargoHeatingOnboardingApiRow(normalized);
}

function applyCargoHeatingFieldAliases(
  target: Record<string, unknown>,
): Record<string, unknown> {
  const merged = { ...target };

  const aliasPairs: Array<[string, string[]]> = [
    ['boiler_make', ['boiler_manufacturer', 'boilerMake']],
    ['max_efficiency_percent', ['max_efficiency', 'maxEfficiency', 'maxEfficiencyPercent']],
    [
      'steam_production_kg_hr',
      ['steam_production_at_max_efficiency', 'steamProductionKgHr', 'steamProductionAtMaxEfficiency'],
    ],
    ['boiler_capacity', ['boilerCapacity']],
    ['deck_steam_pressure', ['deckSteamPressure']],
    ['number_of_boilers', ['numberOfBoilers']],
    ['number_of_tanks', ['numberOfTanks']],
    ['heating_arrangement', ['heatingArrangement']],
    ['steam_pipes_insulation', ['steamPipesInsulation']],
    ['egcs_scrubber', ['egcsScrubber']],
  ];

  aliasPairs.forEach(([canonical, aliases]) => {
    if (isMeaningfulCargoValue(merged[canonical])) {
      return;
    }
    for (const alias of aliases) {
      if (isMeaningfulCargoValue(merged[alias])) {
        merged[canonical] = merged[alias];
        break;
      }
    }
  });

  return merged;
}

function liftCargoHeatingNestedFields(
  target: Record<string, unknown>,
): Record<string, unknown> {
  const merged: Record<string, unknown> = { ...target };
  const nestedKeys = [
    'json',
    'config',
    'cargo_heating',
    'onboarding_form',
    'onboarding_config',
    'cargo_heating_config',
    'onboarding_data',
    'onboardingData',
  ];

  nestedKeys.forEach((key) => {
    let nested = merged[key];
    const parsed = parseNestedJsonObject(nested);
    if (parsed) {
      nested = parsed;
    }
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      Object.assign(merged, nested as Record<string, unknown>);
    }
  });

  const output = merged['output'] as Record<string, unknown> | undefined;
  if (output && typeof output === 'object') {
    merged['tanks'] ??= output['tanks'] ?? output['tank_details'];
    merged['heating_tanks'] ??= merged['tanks'];
    merged['heating_table'] ??=
      output['heating_table'] ?? output['tables'] ?? output['compartments'];
    merged['heatingTable'] ??= merged['heating_table'];
    merged['liquidCargo'] ??=
      output['liquid_cargo'] ?? output['liquidCargo'] ?? output['compartment_metrics'];
    merged['liquid_cargo'] ??= merged['liquidCargo'];
    merged['pipelineSections'] ??=
      output['pipeline_sections'] ?? output['pipelineSections'];
    merged['pipeline_sections'] ??= merged['pipelineSections'];
  }

  return applyCargoHeatingFieldAliases(merged);
}

/** Resolve cargo heating detail for a specific IMO from API/list payloads. */
export function normalizeCargoHeatingDetailRow(
  expectedImo: string,
  data: unknown,
  fallback?: Record<string, unknown>,
): Record<string, unknown> {
  const expected = normalizeImoDigits(expectedImo);
  const candidates: Record<string, unknown>[] = [];

  const collect = (input: unknown): void => {
    if (!input) {
      return;
    }
    if (Array.isArray(input)) {
      input.forEach((item) => collect(item));
      return;
    }
    if (typeof input !== 'object') {
      return;
    }
    const record = input as Record<string, unknown>;
    candidates.push(record);
    [
      'data',
      'vessel',
      'json',
      'config',
      'cargo_heating',
      'onboarding_form',
      'onboarding_config',
      'onboarding_data',
      'onboardingData',
      'result',
    ].forEach((key) => collect(record[key]));
  };

  collect(data);

  let matched =
    expected &&
    candidates.find(
      (row) => normalizeImoDigits(pickImo(row)) === expected,
    );

  if (!matched) {
    matched = normalizeDetailRow(data);
    if (!matched || !Object.keys(matched).length) {
      matched = fallback ?? {};
    }
  }

  const merged = liftCargoHeatingNestedFields({
    ...(fallback ?? {}),
    ...matched,
  });

  if (expected) {
    merged['imo'] = expected;
    merged['vessel_imo'] = expected;
  }
  if (fallback?.['vessel_name'] && !merged['vessel_name']) {
    merged['vessel_name'] = fallback['vessel_name'];
  }

  return merged;
}

/** Normalize GET /port-consumption/vessel-onboarding-details into table rows for selected IMO. */
export function normalizePortOnboardingDetailRows(
  data: unknown,
  imo: string,
  vesselName: string,
): Record<string, unknown>[] {
  if (data == null) {
    return [];
  }

  let rows: Record<string, unknown>[] = [];
  if (Array.isArray(data)) {
    rows = data as Record<string, unknown>[];
  } else if (typeof data === 'object') {
    const record = data as Record<string, unknown>;
    if (Array.isArray(record['data'])) {
      rows = record['data'] as Record<string, unknown>[];
    } else if (Array.isArray(record['details'])) {
      rows = record['details'] as Record<string, unknown>[];
    } else if (Array.isArray(record['results'])) {
      rows = record['results'] as Record<string, unknown>[];
    } else if (
      record['data'] &&
      typeof record['data'] === 'object' &&
      !Array.isArray(record['data'])
    ) {
      rows = [record['data'] as Record<string, unknown>];
    } else if (Object.keys(record).length) {
      rows = [record];
    }
  }

  const normalizedImo = normalizeImoDigits(imo);
  const filtered = normalizedImo
    ? rows.filter((row) => {
        const rowImo = normalizeImoDigits(pickImo(row));
        return !rowImo || rowImo === normalizedImo;
      })
    : rows;

  return filtered.map((row) => ({
    ...row,
    vessel_imo: row['vessel_imo'] ?? row['imo'] ?? normalizedImo,
    vessel_name: row['vessel_name'] ?? row['vesselName'] ?? vesselName,
  }));
}

/** Map quick-onboarding payload to a Detailed Report row when full port details are missing. */
export function mapQuickOnboardingToDetailRow(
  payload: unknown,
  imo: string,
  vesselName: string,
): Record<string, unknown> | null {
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  const root = payload as Record<string, unknown>;
  const data = (
    root['data'] && typeof root['data'] === 'object'
      ? root['data']
      : payload
  ) as Record<string, unknown>;

  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return null;
  }

  const normalizedImo = normalizeImoDigits(data['imo_number'] ?? imo);
  if (!normalizedImo) {
    return null;
  }

  return {
    vessel_imo: normalizedImo,
    vessel_name: data['vessel_name'] ?? vesselName,
    vessel_type: data['type_of_vessel'],
    vessel_class: data['segment'],
    ship_yard: data['builder_where_built'],
    date_built: data['year_built'],
    length_overall: data['lengOfVessel'],
    breadth: data['beam'],
    summer_deadweight: data['loadline_summer_deadweight_metric_tonnes'],
    gross_tonnage: data['grossTonnage'],
  };
}

function formatTableCellValue(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : '—';
  }
  if (typeof value === 'object') {
    return formatCargoApiValue(value);
  }
  return String(value);
}

function formatDetailCellValue(value: string, keys: string[]): string {
  if (value === '—') {
    return value;
  }
  const isDateField = keys.some((key) =>
    /updated_at|submitted_at|last_updated|created_at|report_generated/i.test(key),
  );
  if (!isDateField) {
    return value;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function kvRows(
  row: Record<string, unknown>,
  fields: {
    label: string;
    keys: string[];
    readOnly?: boolean;
    type?: 'text' | 'number';
  }[],
): OnboardingDetailRow[] {
  return fields.map(({ label, keys, readOnly, type }) => ({
    label,
    keys,
    readOnly,
    type,
    value: formatDetailCellValue(pickField(row, ...keys), keys),
  }));
}

function liquidCargoTableFromArray(
  items: unknown,
): OnboardingDetailTable | null {
  const built = buildLiquidCargoDynamicTable(items);
  if (!built) {
    return null;
  }

  return {
    headers: built.headers,
    rows: built.rows,
    columns: built.columns,
    columnKeys: built.columnKeys,
    rawRows: built.rawRows,
    dynamic: true,
    dataKey: 'liquid_cargo',
    editable: true,
  };
}

function tableFromArray(
  items: unknown,
  columns: { header: string; key: string; keys?: string[] }[],
  options?: { dataKey?: string; editable?: boolean },
): OnboardingDetailTable | null {
  if (!Array.isArray(items) || !items.length) {
    return null;
  }
  return {
    headers: columns.map((c) => c.header),
    columns: columns.map((c) => ({ header: c.header, key: c.key })),
    dataKey: options?.dataKey,
    editable: options?.editable ?? true,
    rows: items
      .map((item) => {
        const row = item as Record<string, unknown>;
        return columns.map((c) => {
          for (const key of c.keys?.length ? c.keys : [c.key]) {
            const value = row[key];
            if (value === null || value === undefined || value === '') {
              continue;
            }
            return formatTableCellValue(value);
          }
          return '—';
        });
      })
      .filter((cells) => cells.some((cell) => cell !== '—')),
  };
}

function pipelineSectionsTable(
  row: Record<string, unknown>,
): OnboardingDetailTable | null {
  const sections = row['pipelineSections'] ?? row['pipeline_sections'];
  if (!Array.isArray(sections) || !sections.length) {
    return null;
  }

  const columns = [
    { header: 'Segment', key: 'segment' },
    { header: 'Thickness (mm)', key: 'thickness' },
    { header: 'Length (m)', key: 'length' },
    { header: 'Steam Insulation', key: 'insulation' },
  ];

  const rows = (sections as Record<string, unknown>[]).map((section) => {
    const thickness = section['pipeline_thickness_values_mm'] ??
      section['pipeline_thickness_mm'] ??
      section['pipelineThicknessMm'];
    const length = section['length_values_m'] ??
      section['length_m'] ??
      section['lengthM'];
    const formatValues = (value: unknown): string => {
      if (Array.isArray(value)) {
        return value.map((entry) => String(entry ?? '')).filter(Boolean).join(', ');
      }
      return value == null || value === '' ? '—' : String(value);
    };

    return [
      pickField(section, 'segment_name', 'segmentName'),
      formatValues(thickness),
      formatValues(length),
      pickField(section, 'steam_insulation', 'steamInsulation'),
    ];
  });

  return {
    headers: columns.map((c) => c.header),
    columns,
    dataKey: 'pipelineSections',
    editable: false,
    rows,
  };
}

export function applyOnboardingSectionSave(
  source: Record<string, unknown>,
  payload: OnboardingSectionSavePayload,
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...source };

  payload.rows.forEach((row) => {
    if (row.readOnly || !row.keys?.length) {
      return;
    }
    const raw = isEmptyOnboardingDisplayValue(row.value) ? '' : row.value;
    let parsedValue: unknown = raw;
    if (row.type === 'number') {
      const parsed = Number(String(raw).replace(/,/g, ''));
      parsedValue = Number.isFinite(parsed) ? parsed : raw;
    }
    row.keys.forEach((key) => {
      next[key] = parsedValue;
      syncEmissionField(next, key, parsedValue);
    });
  });

  if (payload.table?.dataKey && payload.table.columns.length) {
    const dataKey = payload.table.dataKey;
    const existing = next[dataKey] ?? aliasDataKeyValue(next, dataKey);
    const baseItems = Array.isArray(existing) ? [...existing] : [];
    const updated = payload.table.rows.map((cells, index) => {
      const current =
        (baseItems[index] as Record<string, unknown> | undefined) ?? {};
      const mapped: Record<string, unknown> = { ...current };
      payload.table!.columns.forEach((column, columnIndex) => {
        const raw = cells[columnIndex];
        mapped[column.key] = isEmptyOnboardingDisplayValue(raw) ? '' : raw;
      });
      return mapped;
    });
    next[dataKey] = updated;
    mirrorArrayDataKey(next, dataKey, updated);
  }

  return next;
}

function aliasDataKeyValue(
  row: Record<string, unknown>,
  dataKey: string,
): unknown {
  const aliases: Record<string, string[]> = {
    tanks: ['heating_tanks'],
    heating_tanks: ['tanks'],
    heating_table: ['heatingTable'],
    heatingTable: ['heating_table'],
    liquidCargo: ['liquid_cargo'],
    liquid_cargo: ['liquidCargo'],
    pipelineSections: ['pipeline_sections'],
    pipeline_sections: ['pipelineSections'],
  };
  for (const alias of aliases[dataKey] ?? []) {
    if (Array.isArray(row[alias])) {
      return row[alias];
    }
  }
  return undefined;
}

function mirrorArrayDataKey(
  row: Record<string, unknown>,
  dataKey: string,
  value: unknown,
): void {
  const mirrors: Record<string, string[]> = {
    tanks: ['heating_tanks'],
    heating_tanks: ['tanks'],
    heating_table: ['heatingTable'],
    heatingTable: ['heating_table'],
    liquidCargo: ['liquid_cargo'],
    liquid_cargo: ['liquidCargo'],
    pipelineSections: ['pipeline_sections'],
    pipeline_sections: ['pipelineSections'],
  };
  (mirrors[dataKey] ?? []).forEach((alias) => {
    row[alias] = value;
  });
}

function syncEmissionField(
  row: Record<string, unknown>,
  key: string,
  value: unknown,
): void {
  const emissionKeys = new Set([
    ...Object.keys(EMISSION_API_FIELD_MAP),
    ...Object.values(EMISSION_API_FIELD_MAP),
    'co2_baseline',
    'co2_actual',
    'eeoi_enabled',
    'eeoi_reference',
    'cii_rating',
    'cii_required',
    'iceClass',
    'ice_class',
  ]);
  if (!emissionKeys.has(key)) {
    return;
  }
  const emission = {
    ...((row['emission'] as Record<string, unknown>) ?? {}),
  };
  emission[key] = value;
  const snakeEntry = Object.entries(EMISSION_API_FIELD_MAP).find(
    ([, camel]) => camel === key,
  );
  if (snakeEntry) {
    emission[snakeEntry[0]] = value;
  }
  const camelEntry = EMISSION_API_FIELD_MAP[key];
  if (camelEntry) {
    emission[camelEntry] = value;
  }
  row['emission'] = emission;
  row['emission_parameters'] = emission;
}
export function buildEmissionDetailApiConfig(
  detail: Record<string, unknown>,
  original?: Record<string, unknown> | null,
): Record<string, unknown> {
  return buildEmissionOnboardingPayloadFromDetail(detail, original);
}

export function buildCargoHeatingDetailApiConfig(
  detail: Record<string, unknown>,
  original?: Record<string, unknown> | null,
): Record<string, unknown> {
  return buildCargoHeatingOnboardingPayloadFromDetail(detail, original);
}

function uploadedFileRows(row: Record<string, unknown>): { label: string; value: string }[] {
  return extractUploadedFilesFromRow(row).map((file) => ({
    label: file.documentType,
    value: file.fileName,
  }));
}

export function buildCargoHeatingDetailSections(
  row: Record<string, unknown>,
): OnboardingDetailSection[] {
  return buildDynamicCargoHeatingDetailSections(row);
}

export function buildEmissionDetailSections(
  row: Record<string, unknown>,
): OnboardingDetailSection[] {
  const emission =
    (row['emission'] as Record<string, unknown>) ??
    (row['emission_parameters'] as Record<string, unknown>) ??
    row;

  return [
    {
      id: 'vessel-info',
      title: 'Vessel Configuration',
      rows: kvRows(row, [
        { label: 'Vessel Name', keys: ['vessel_name', 'vesselName'], readOnly: true },
        { label: 'IMO Number', keys: ['imo', 'imo_number', 'vessel_imo'], readOnly: true },
        { label: 'Client', keys: ['client', 'client_name', 'client_id'], readOnly: true },
        { label: 'Segment', keys: ['segment'], readOnly: true },
        { label: 'Emission Type', keys: ['emission_type', 'type_of_ship', 'vessel_type'] },
        { label: 'Status', keys: ['onboarding_status', 'status'], readOnly: true },
        { label: 'Last Updated', keys: ['updated_at', 'submitted_at', 'last_updated'], readOnly: true },
      ]),
    },
    {
      id: 'fuel-consumption',
      title: 'Fuel Consumption Details',
      rows: kvRows(emission, [
        { label: 'SFC ME', keys: ['sfcme', 'sfc_me'], type: 'number' },
        { label: 'SFC ME (Lim)', keys: ['sfcmeLim', 'sfcme_lim', 'sfc_me_lim'], type: 'number' },
        { label: 'SFE AE', keys: ['sfeae', 'sfe_ae'], type: 'number' },
      ]),
    },
    {
      id: 'emission-factors',
      title: 'Emission Factors',
      rows: kvRows(emission, [
        { label: 'CF ME', keys: ['cfme', 'cf_me'], type: 'number' },
        { label: 'CF AE', keys: ['cfae', 'cf_ae'], type: 'number' },
        { label: 'Fi', keys: ['fi'], type: 'number' },
        { label: 'Fc', keys: ['fc'], type: 'number' },
        { label: 'Fj', keys: ['fj'] },
        { label: 'Fw', keys: ['fw'] },
        { label: 'Fm', keys: ['fm'] },
        { label: 'Ficsr', keys: ['ficsr'] },
      ]),
    },
    {
      id: 'co2',
      title: 'CO₂ Calculations',
      rows: kvRows(emission, [
        { label: 'CO₂ Baseline', keys: ['co2_baseline'] },
        { label: 'CO₂ Actual', keys: ['co2_actual'] },
      ]),
    },
    {
      id: 'eeoi',
      title: 'EEOI Configuration',
      rows: kvRows(emission, [
        { label: 'EEOI Enabled', keys: ['eeoi_enabled'] },
        { label: 'EEOI Reference', keys: ['eeoi_reference'] },
      ]),
    },
    {
      id: 'cii',
      title: 'CII Configuration',
      rows: kvRows(emission, [
        { label: 'CII Rating', keys: ['cii_rating'] },
        { label: 'CII Required', keys: ['cii_required'] },
        { label: 'Ice Class', keys: ['iceClass', 'ice_class'] },
      ]),
    },
    {
      id: 'egcs',
      title: 'EGCS Information',
      rows: kvRows(row, [
        { label: 'EGCS Fitted', keys: ['egcs_fitted', 'exhaust_gas_cleaning_system'] },
        { label: 'Scrubber Type', keys: ['scrubber_type'] },
      ]),
    },
    {
      id: 'fuel-types',
      title: 'Fuel Type Configuration',
      rows: kvRows(emission, [
        { label: 'Fuel Type (ME)', keys: ['fuelTypeMe', 'fuel_type_me'] },
        { label: 'Fuel Type (AE)', keys: ['fuelTypeAe', 'fuel_type_ae'] },
      ]),
    },
    {
      id: 'engine-details',
      title: 'Engine Details',
      rows: kvRows(emission, [
        { label: 'Speed Vref (knots)', keys: ['speedVref', 'speed_vref'] },
        { label: 'MCR MR (kW)', keys: ['mcrmr', 'mcr_mr'] },
        { label: 'MCR ME Lim (kW)', keys: ['mcrmeLim', 'mcr_me_lim'] },
        { label: 'Pme (kW)', keys: ['pme'] },
        { label: 'Pme Lim (kW)', keys: ['pmeLim', 'pme_lim'] },
        { label: 'Ppto (kW)', keys: ['ppto'] },
        { label: 'Pae (kW)', keys: ['pae'] },
      ]),
    },
    {
      id: 'emission-reports',
      title: 'Generated Emission Reports',
      rows: kvRows(row, [
        { label: 'Latest Report', keys: ['latest_emission_report'], readOnly: true },
        { label: 'Report Generated At', keys: ['report_generated_at'], readOnly: true },
      ]),
      readOnly: true,
      editable: false,
    },
    {
      id: 'uploaded-files',
      title: 'Uploaded Files',
      rows: uploadedFileRows(row).map((fileRow) => ({ ...fileRow, readOnly: true })),
      readOnly: true,
      editable: false,
    },
    {
      id: 'benchmark',
      title: 'Benchmark Parameters',
      rows: kvRows(row, [
        { label: 'Benchmark Reference', keys: ['benchmark_reference'] },
        { label: 'Benchmark Notes', keys: ['benchmark_notes'] },
      ]),
    },
  ];
}

export function formatOnboardingStatus(status: string): string {
  if (!status || status === '—') {
    return '—';
  }
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatLastUpdated(row: Record<string, unknown>): string {
  const raw = pickField(row, 'updated_at', 'submitted_at', 'last_updated', 'created_at');
  if (raw === '—') {
    return raw;
  }
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? raw : date.toLocaleString();
}

export interface PortVesselDetailFieldDef {
  label: string;
  keys: string[];
  formKey: string;
  type?: 'text' | 'number' | 'textarea';
  readOnly?: boolean;
  required?: boolean;
}

export interface PortVesselDetailGroupDef {
  id: string;
  title: string;
  fields: PortVesselDetailFieldDef[];
}

export const PORT_ONBOARDING_DETAIL_GROUPS: PortVesselDetailGroupDef[] = [
  {
    id: 'general',
    title: 'General Details',
    fields: [
      { label: 'Vessel Name', keys: ['vessel_name'], formKey: 'vessel_name', required: true },
      { label: 'IMO Number', keys: ['vessel_imo'], formKey: 'vessel_imo', readOnly: true },
      { label: 'Client', keys: ['client'], formKey: 'client' },
      { label: 'Segment', keys: ['segment', 'vessel_class'], formKey: 'segment' },
      { label: 'Vessel Type', keys: ['vessel_type'], formKey: 'vessel_type' },
      { label: 'Vessel Class', keys: ['vessel_class'], formKey: 'vessel_class' },
      { label: 'Date Built', keys: ['date_built'], formKey: 'date_built' },
      { label: 'Ship Yard', keys: ['ship_yard'], formKey: 'ship_yard' },
      { label: 'Vessel Status', keys: ['vessel_status'], formKey: 'vessel_status' },
      { label: 'Created At', keys: ['created_at'], formKey: 'created_at', readOnly: true },
      { label: 'Created By', keys: ['created_by'], formKey: 'created_by', readOnly: true },
    ],
  },
  {
    id: 'dimensions',
    title: 'Dimensions',
    fields: [
      { label: 'Length Overall', keys: ['length_overall'], formKey: 'length_overall', type: 'number' },
      { label: 'Breadth', keys: ['breadth'], formKey: 'breadth', type: 'number' },
      { label: 'Summer Draft', keys: ['summer_draft'], formKey: 'summer_draft', type: 'number' },
      { label: 'Summer Deadweight', keys: ['summer_deadweight'], formKey: 'summer_deadweight', type: 'number' },
    ],
  },
  {
    id: 'cargo',
    title: 'Cargo Details',
    fields: [
      { label: 'No. of Cargo Oil Pumps', keys: ['number_of_cargo_oil_pumps'], formKey: 'number_of_cargo_oil_pumps', type: 'number' },
      { label: 'Cargo Pump Type', keys: ['cargo_pump_type'], formKey: 'cargo_pump_type' },
      { label: 'Cargo Pump Capacity', keys: ['cargo_pump_capacity'], formKey: 'cargo_pump_capacity', type: 'number' },
      { label: 'Cargo Pump Head', keys: ['cargo_pump_head'], formKey: 'cargo_pump_head', type: 'number' },
      { label: 'Framo Cargo Pump', keys: ['framo_cargo_pump'], formKey: 'framo_cargo_pump' },
      { label: 'Cargo Tank Capacity', keys: ['cargo_tank_capacity'], formKey: 'cargo_tank_capacity', type: 'number' },
      { label: 'Max Discharge Rate', keys: ['max_discharge_rate'], formKey: 'max_discharge_rate', type: 'number' },
      { label: 'Max Loading Rate', keys: ['max_loading_rate'], formKey: 'max_loading_rate', type: 'number' },
    ],
  },
  {
    id: 'ballast',
    title: 'Ballast Details',
    fields: [
      { label: 'Ballast Pump Type', keys: ['ballast_pump_type'], formKey: 'ballast_pump_type' },
      { label: 'Ballast Pump Capacity', keys: ['ballast_pump_capacity'], formKey: 'ballast_pump_capacity', type: 'number' },
      { label: 'Ballast Pump Head', keys: ['ballast_pump_head'], formKey: 'ballast_pump_head', type: 'number' },
      { label: 'Framo Ballast Pump', keys: ['framo_ballast_pump'], formKey: 'framo_ballast_pump' },
      { label: 'Ballast Tank Capacity', keys: ['ballast_tank_capacity'], formKey: 'ballast_tank_capacity', type: 'number' },
    ],
  },
  {
    id: 'engines',
    title: 'Engine Details',
    fields: [
      { label: 'Main Engine Make', keys: ['main_engine_make'], formKey: 'main_engine_make' },
      { label: 'Main Engine Capacity', keys: ['main_engine_capacity'], formKey: 'main_engine_capacity', type: 'number' },
      { label: 'Auxiliary Engine Make', keys: ['auxiliary_engine_make'], formKey: 'auxiliary_engine_make' },
      { label: 'Auxiliary Engine Capacity', keys: ['auxiliary_engine_capacity'], formKey: 'auxiliary_engine_capacity', type: 'number' },
      { label: 'Diesel Power Packs', keys: ['power_packs_diesel'], formKey: 'power_packs_diesel', type: 'number' },
      { label: 'Electric Power Packs', keys: ['power_packs_electric'], formKey: 'power_packs_electric', type: 'number' },
      { label: 'Diversity Factor', keys: ['vessel_diversity_factor'], formKey: 'vessel_diversity_factor', type: 'number' },
    ],
  },
  {
    id: 'boiler',
    title: 'Boiler Details',
    fields: [
      { label: 'Boiler Make', keys: ['boiler_make'], formKey: 'boiler_make' },
      { label: 'Number of Boilers', keys: ['number_of_boilers'], formKey: 'number_of_boilers', type: 'number' },
      { label: 'Boiler Capacity (kg/hr)', keys: ['boiler_capacity_kg_hr'], formKey: 'boiler_capacity_kg_hr', type: 'number' },
      { label: 'Boiler Load %', keys: ['boiler_load_percent'], formKey: 'boiler_load_percent', type: 'number' },
      { label: 'Composite Boiler Capacity in Kg/hr', keys: ['composite_boiler_consumption_per_day', 'composite_boiler_consumption_mt_day', 'compositeBoilerConsumptionPerDay'], formKey: 'composite_boiler_consumption_per_day', type: 'number' },
    ],
  },
  {
    id: 'operations',
    title: 'Operations & Load Parameters',
    fields: [
      { label: 'IGS', keys: ['igs'], formKey: 'igs' },
      { label: 'Tank Cleaning Avg. Peak', keys: ['tank_cleaning_avg_peak'], formKey: 'tank_cleaning_avg_peak', type: 'number' },
      { label: 'Tank Cleaning Electrical Load', keys: ['tank_cleaning_electrical_load'], formKey: 'tank_cleaning_electrical_load', type: 'number' },
      { label: 'Electrical Load Avg. Peak', keys: ['electrical_load_avg_peak'], formKey: 'electrical_load_avg_peak', type: 'number' },
      { label: 'Idle Avg. Peak', keys: ['idle_avg_peak'], formKey: 'idle_avg_peak', type: 'number' },
      { label: 'Idle Electrical Load', keys: ['idle_electrical_load'], formKey: 'idle_electrical_load', type: 'number' },
      { label: 'Normal Sea Avg. Peak', keys: ['normal_sea_avg_peak'], formKey: 'normal_sea_avg_peak', type: 'number' },
      { label: 'Normal Sea Electrical Load', keys: ['normal_sea_electrical_load'], formKey: 'normal_sea_electrical_load', type: 'number' },
      { label: 'Maneuvering Avg. Peak', keys: ['maneuvering_avg_peak'], formKey: 'maneuvering_avg_peak', type: 'number' },
      { label: 'Maneuvering Electrical Load', keys: ['maneuvering_electrical_load'], formKey: 'maneuvering_electrical_load', type: 'number' },
      { label: 'Cargo Ops Avg. Peak', keys: ['cargo_ops_avg_peak'], formKey: 'cargo_ops_avg_peak', type: 'number' },
      { label: 'Cargo Ops Electrical Load', keys: ['cargo_ops_electrical_load'], formKey: 'cargo_ops_electrical_load', type: 'number' },
    ],
  },
  {
    id: 'technical',
    title: 'Technical Parameters',
    fields: [
      { label: 'Cargo Pump Steam Parameters', keys: ['cargo_pump_steam_parameters'], formKey: 'cargo_pump_steam_parameters', type: 'textarea', readOnly: true },
      { label: 'Loading Avg. Peak Params', keys: ['loading_avg_peak_params'], formKey: 'loading_avg_peak_params', type: 'textarea', readOnly: true },
      { label: 'Loading Electrical Load Params', keys: ['loading_electrical_load_params'], formKey: 'loading_electrical_load_params', type: 'textarea', readOnly: true },
      { label: 'Auxiliary Engine Technical Parameters', keys: ['auxiliary_engine_technical_parameters'], formKey: 'auxiliary_engine_technical_parameters', type: 'textarea', readOnly: true },
      { label: 'Ballast Pump Steam Parameters', keys: ['ballast_pump_steam_parameters'], formKey: 'ballast_pump_steam_parameters', type: 'textarea', readOnly: true },
      { label: 'Ballast Pump Electrical Parameters', keys: ['ballast_pump_electrical_parameters'], formKey: 'ballast_pump_electrical_parameters', type: 'textarea', readOnly: true },
      { label: 'Main Engine Technical Parameters', keys: ['main_engine_technical_parameters'], formKey: 'main_engine_technical_parameters', type: 'textarea', readOnly: true },
      { label: 'Boiler Parameters', keys: ['boiler_parameters'], formKey: 'boiler_parameters', type: 'textarea', readOnly: true },
      { label: 'IGG Parameters', keys: ['igg_parameters'], formKey: 'igg_parameters', type: 'textarea', readOnly: true },
      { label: 'Inerting Parameters', keys: ['inerting_parameters'], formKey: 'inerting_parameters', type: 'textarea', readOnly: true },
      { label: 'Maneuvering Technical Parameters', keys: ['maneuvering_technical_parameters'], formKey: 'maneuvering_technical_parameters', type: 'textarea', readOnly: true },
      { label: 'TCP Parameters', keys: ['vessel_tcp_parameters'], formKey: 'vessel_tcp_parameters', type: 'textarea', readOnly: true },
      { label: 'Speed Coefficients', keys: ['vessel_speed_coefficients'], formKey: 'vessel_speed_coefficients', type: 'textarea', readOnly: true },
    ],
  },
];

export function formatVesselDetailDisplayValue(value: unknown): string {
  if (value == null || value === '') {
    return '—';
  }
  if (typeof value === 'object') {
    if (Array.isArray(value)) {
      return value.length ? JSON.stringify(value, null, 2) : '—';
    }
    const entries = Object.entries(value as Record<string, unknown>);
    if (!entries.length) {
      return '—';
    }
    return entries.map(([key, entryValue]) => `${key}: ${entryValue}`).join('\n');
  }
  return String(value);
}

export function rawVesselFieldValue(
  row: Record<string, unknown> | null | undefined,
  ...keys: string[]
): string {
  if (!row) {
    return '';
  }
  for (const key of keys) {
    const value = row[key];
    if (value != null && value !== '') {
      if (typeof value === 'object') {
        return JSON.stringify(value);
      }
      return String(value);
    }
  }
  return '';
}
